import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import type { DynamicListsApi } from './api'
import { RuleEditor, type ListOption } from './RuleEditor'
import { newRule, ruleProblem, templateText } from './rule'
import { useAdoptPreview } from './hooks'
import { Modal } from './shared'
import type { AdoptableList, Catalog, DynamicList, FilterType, Rule } from './types'
import { LoadingBlock, BusyLabel, Spinner } from '../components/Spinner'

interface Props {
  api: DynamicListsApi
  accountId: string
  catalog: Catalog
  /** The Account's lists with adoptability; null while loading. */
  adoptable: AdoptableList[] | null
  adoptableError: string
  lists: ListOption[] | null
  onDone: (list: DynamicList) => void
  onClose: () => void
}

/** Adoption (CONTEXT.md): pick a custom list, set the rule, see what the first update would do. */
export function AdoptDialog({ api, accountId, catalog, adoptable, adoptableError, lists, onDone, onClose }: Props) {
  const { t } = useTranslation('dynamicLists')
  const [picked, setPicked] = useState<AdoptableList | null>(null)
  const [rule, setRule] = useState<Rule>(newRule())
  const [templateKey, setTemplateKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const available = useMemo(() => new Set(catalog.filters.filter((f) => f.available).map((f) => f.type as FilterType)), [catalog])
  const problem = picked ? ruleProblem(rule, available) : null
  const preview = useAdoptPreview(api, accountId, picked?.id ?? null, rule, !problem)

  const suggestedName = (key: string) => {
    const tpl = catalog.templates.find((x) => x.key === key)
    return tpl ? templateText(tpl, t).name : key
  }

  const choose = (l: AdoptableList) => {
    const tpl = catalog.templates.find((x) => x.key === l.suggested_template)
    setPicked(l)
    setRule(tpl ? tpl.rule : newRule())
    setTemplateKey(tpl ? tpl.key : null)
  }

  const submit = async () => {
    if (!picked) return
    setSaving(true)
    setError('')
    try {
      onDone(await api.adopt(accountId, picked.id, { rule, schedule: 'regular', template_key: templateKey }))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setSaving(false)
    }
  }

  if (!picked) {
    return (
      <Modal title={t('adopt.title')} onClose={onClose} wide>
        <p className="dl-hint">{t('adopt.intro')}</p>
        {adoptableError && <p className="dl-error">{adoptableError}</p>}
        {!adoptable && !adoptableError && <LoadingBlock pad="16px 0" size={20} label={t('loading')} />}
        <ul className="dl-pick-list" aria-label={t('adopt.pick')}>
          {(adoptable ?? []).map((l) => (
            <li key={l.id}>
              <button type="button" className="dl-pick" disabled={!l.adoptable} onClick={() => choose(l)}>
                <FeatherIcon name="list" size={14} />
                <span className="dl-pick-main">
                  <span className="dl-ellipsis dl-strong">{l.name}</span>
                  <span className="dl-muted">
                    {l.adoptable
                      ? l.users_count != null
                        ? t('adopt.members', { count: l.users_count })
                        : t('adopt.unknownMembers')
                      : t('adopt.notAdoptable', { reason: l.reason })}
                  </span>
                </span>
                {l.adoptable && l.suggested_template && (
                  <span className="dl-badge">{t('adopt.template', { name: suggestedName(l.suggested_template) })}</span>
                )}
                {l.adoptable && <FeatherIcon name="chevron-right" size={14} className="dl-muted" />}
              </button>
            </li>
          ))}
        </ul>
      </Modal>
    )
  }

  const p = preview.data
  return (
    <Modal
      side
      title={t('adopt.ruleFor', { name: picked.name })}
      onClose={onClose}
      footer={
        <>
          {error && <p className="dl-error dl-foot-error">{error}</p>}
          <button type="button" className="dl-btn dl-btn--ghost" onClick={() => setPicked(null)}>
            {t('back')}
          </button>
          <button type="button" className="dl-btn dl-btn--primary" disabled={Boolean(problem) || saving} onClick={() => void submit()}>
            <BusyLabel busy={saving} label={t('saving')}>{t('adopt.submit')}</BusyLabel>
          </button>
        </>
      }
    >
      <div className="dl-editor">
        <RuleEditor
          rule={rule}
          onChange={(r) => {
            setRule(r)
            setTemplateKey(null)
          }}
          filters={catalog.filters}
          lists={lists}
          ownListId={picked.id}
        />
        <section className="dl-preview" aria-live="polite" aria-label={t('preview.title')}>
          <header className="dl-preview-head">
            <h3 className="dl-h3">{t('adopt.change')}</h3>
            {preview.loading && <Spinner size={14} label={t('preview.updating')} />}
          </header>
          {problem ? (
            <p className="dl-warn">{t(problem.key, problem.values)}</p>
          ) : preview.error ? (
            <p className="dl-error">{preview.error}</p>
          ) : p && !p.facts.last_pass_finished_at ? (
            <p className="dl-muted">{t('preview.noFacts')}</p>
          ) : p ? (
            <>
              <ul className="dl-change">
                <li className="dl-change-add">{t('adopt.add', { count: p.add })}</li>
                <li className="dl-change-remove">{t('adopt.remove', { count: p.remove })}</li>
                <li>{t('adopt.keep', { count: Math.max(0, p.current - p.remove) })}</li>
                {p.not_fans != null && p.not_fans > 0 && <li className="dl-change-remove">{t('adopt.notFans', { count: p.not_fans })}</li>}
              </ul>
              {p.remove_unscored > 0 && <p className="dl-warn">{t('adopt.removeUnscored', { count: p.remove_unscored })}</p>}
              {p.unscored > 0 && <p className="dl-muted">{t('preview.unscored', { count: p.unscored })}</p>}
            </>
          ) : (
            <LoadingBlock pad="16px 0" size={20} label={t('loading')} />
          )}
          <p className="dl-hint">{t('toggle.adoptUndo')}</p>
        </section>
      </div>
    </Modal>
  )
}
